import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Languages } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface TitleTranslationHelperProps {
  currentTitle: string;
  targetLanguageCode: string;
  targetLanguageName: string;
  onSuggestedTitle: (title: string) => void;
  /** Story details, sent as context so the title uses the story's variant/name spellings. */
  storyDetails?: string;
  autoTranslate?: boolean; // Auto-translate when language changes
}

export function TitleTranslationHelper({
  currentTitle,
  targetLanguageCode,
  targetLanguageName,
  onSuggestedTitle,
  storyDetails = '',
  autoTranslate = true
}: TitleTranslationHelperProps) {
  const [isTranslating, setIsTranslating] = useState(false);
  const { toast } = useToast();
  // Initialize to 'en' so first non-English selection triggers translation
  const previousLanguageRef = useRef<string>('en');
  const lastTranslatedTitleRef = useRef<string>('');

  // Memoized translate function to avoid stale closures
  const translateTitle = useCallback(async (title: string, langCode: string, langName: string) => {
    if (!title.trim()) {
      return;
    }

    setIsTranslating(true);

    try {
      const response = await fetch('/api/translate-title', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: title,
          targetLanguage: langCode,
          context: storyDetails
        })
      });

      if (!response.ok) {
        throw new Error('Translation failed');
      }

      const data = await response.json();

      if (data.translatedTitle) {
        onSuggestedTitle(data.translatedTitle);
        toast({
          title: "Title translated!",
          description: `${langName} title: "${data.translatedTitle}"`,
        });
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast({
        title: "Translation failed",
        description: "Could not translate the title. Please try entering it manually.",
        variant: "destructive"
      });
    } finally {
      setIsTranslating(false);
    }
  }, [onSuggestedTitle, storyDetails, toast]);

  // Auto-translate when language changes to non-English
  useEffect(() => {
    // Only auto-translate if:
    // 1. Auto-translate is enabled
    // 2. There's a title to translate (at least 3 chars for meaningful translation)
    // 3. Language actually changed from previous
    // 4. Target language is not English
    // 5. We haven't already translated this exact title to this language
    const titleToTranslate = currentTitle.trim();
    const shouldTranslate = autoTranslate &&
        titleToTranslate.length >= 3 &&
        targetLanguageCode !== previousLanguageRef.current &&
        targetLanguageCode !== 'en' &&
        lastTranslatedTitleRef.current !== `${titleToTranslate}:${targetLanguageCode}`;

    if (shouldTranslate) {
      console.log(`[TitleTranslation] Auto-translating "${titleToTranslate}" to ${targetLanguageCode}`);
      previousLanguageRef.current = targetLanguageCode;
      lastTranslatedTitleRef.current = `${titleToTranslate}:${targetLanguageCode}`;
      translateTitle(titleToTranslate, targetLanguageCode, targetLanguageName);
    } else if (targetLanguageCode !== previousLanguageRef.current) {
      // Just update the ref if language changed but we're not auto-translating
      previousLanguageRef.current = targetLanguageCode;
    }
  }, [targetLanguageCode, autoTranslate, currentTitle, targetLanguageName, translateTitle]);

  const handleTranslate = async () => {
    if (!currentTitle.trim()) {
      toast({
        title: "No title to translate",
        description: "Please enter a title first",
        variant: "destructive"
      });
      return;
    }
    translateTitle(currentTitle, targetLanguageCode, targetLanguageName);
  };

  // Only show for non-English languages
  if (targetLanguageCode === 'en') {
    return null;
  }

  return (
    <div className="mt-2">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={handleTranslate}
        disabled={isTranslating || !currentTitle.trim()}
      >
        {isTranslating ? (
          <>
            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
            Translating...
          </>
        ) : (
          <>
            <Languages className="mr-2 h-3 w-3" />
            Suggest {targetLanguageName} title
          </>
        )}
      </Button>
    </div>
  );
}
